import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Loader2, ShieldCheck, ShieldOff, Info } from "lucide-react";
import { api, formatApiError as formatErr } from "../lib/api";
import { PLACEMENT_INFO_SECTIONS, SECTION_STATUS_LABEL, SECTION_STATUS_CLASS } from "../data/placementInfoFields";

/* Family Care Office — "Getting Ready for Care" shared Placement record.
   Family-owned, editable section-by-section; shared read-only with
   explicitly-authorised carers via the Sharing panel below.
   /workspace/family-circle/placement/:placementId/getting-ready */

function SectionEditor({ section, values, sectionStatus, onSaved }) {
  const [draft, setDraft] = useState(() => {
    const d = {};
    section.fields.forEach((f) => { d[f.key] = values[f.key] || ""; });
    return d;
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/api/care-placement-info/by-placement/${values.placement_id}`, {
        section: section.key,
        fields: draft,
      });
      onSaved(data);
      setSavedAt(new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }));
      toast.success("Saved");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid={`placement-info-section-${section.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <h2 className="font-display text-xl font-semibold">{section.label}</h2>
        <span className={`pill text-xs ${SECTION_STATUS_CLASS[sectionStatus]}`} data-testid={`placement-info-status-${section.key}`}>
          {SECTION_STATUS_LABEL[sectionStatus]}
        </span>
      </div>
      {section.note && (
        <p className="text-xs text-ink-soft mb-5 flex items-start gap-1.5 max-w-2xl">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {section.note}
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-5">
        {section.fields.map((f) => (
          <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
            <label className="text-sm font-medium text-ink" htmlFor={`pi-${f.key}`}>{f.label}</label>
            {f.type === "textarea" ? (
              <textarea id={`pi-${f.key}`} rows={2} value={draft[f.key]}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                data-testid={`placement-info-field-${f.key}`}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple/30 focus:border-purple/40" />
            ) : (
              <input id={`pi-${f.key}`} type="text" value={draft[f.key]}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                data-testid={`placement-info-field-${f.key}`}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple/30 focus:border-purple/40" />
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-3">
        <button type="button" onClick={save} disabled={saving} className="btn-purple text-sm" data-testid={`placement-info-save-${section.key}`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save this section
        </button>
        {savedAt && <span className="text-xs text-teal">Saved · last saved {savedAt}</span>}
      </div>
    </section>
  );
}

function SharingPanel({ placementId }) {
  const [carers, setCarers] = useState(null);
  const [busy, setBusy] = useState({});

  const load = () => {
    api.get(`/api/care-placement-info/by-placement/${placementId}/shares`)
      .then((r) => setCarers(r.data.carers || []))
      .catch(() => setCarers([]));
  };
  useEffect(load, [placementId]);

  const toggle = async (carer) => {
    setBusy((b) => ({ ...b, [carer.carer_email]: true }));
    try {
      const action = carer.shared ? "revoke" : "grant";
      await api.post(`/api/care-placement-info/by-placement/${placementId}/shares/${encodeURIComponent(carer.carer_email)}/${action}`);
      toast.success(carer.shared ? "Access revoked" : "Shared with this carer");
      load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy((b) => ({ ...b, [carer.carer_email]: false }));
    }
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid="placement-info-sharing-panel">
      <h2 className="font-display text-xl font-semibold">Sharing</h2>
      <p className="text-sm text-ink-muted mt-2 mb-5 max-w-2xl">
        Having a Discussion Room does not give a carer access to this record. Choose exactly who can see it —
        you can revoke access for any carer at any time, without affecting anyone else.
      </p>
      {carers === null ? (
        <p className="text-sm text-ink-soft">Loading…</p>
      ) : carers.length === 0 ? (
        <p className="text-sm text-ink-soft">No carers connected to this Placement yet.</p>
      ) : (
        <ul className="space-y-3" data-testid="placement-info-carer-list">
          {carers.map((c) => (
            <li key={c.carer_email} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-4 py-3" data-testid={`placement-info-carer-row-${c.carer_email}`}>
              <div>
                <p className="text-sm font-medium">{c.carer_name || c.carer_email}</p>
                <p className="text-xs text-ink-soft">{c.carer_email}</p>
              </div>
              <button
                type="button"
                onClick={() => toggle(c)}
                disabled={busy[c.carer_email]}
                className={c.shared ? "btn-outline text-sm !py-1.5 !px-3" : "btn-purple text-sm !py-1.5 !px-3"}
                data-testid={`placement-info-share-toggle-${c.carer_email}`}
              >
                {c.shared ? <><ShieldOff className="w-3.5 h-3.5" /> Revoke access</> : <><ShieldCheck className="w-3.5 h-3.5" /> Share with this carer</>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function PlacementInfoRecord() {
  const { placementId } = useParams();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/api/care-placement-info/by-placement/${placementId}`)
      .then((r) => setRecord(r.data))
      .catch((err) => setError(formatErr(err)))
      .finally(() => setLoading(false));
  }, [placementId]);

  if (loading) {
    return <div className="container-jcc py-24 text-center text-ink-muted" data-testid="placement-info-loading"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…</div>;
  }
  if (error) {
    return (
      <div className="container-jcc py-24 max-w-lg text-center" data-testid="placement-info-error">
        <p className="text-rose-700">{error}</p>
        <Link to="/workspace/family-circle/placements" className="btn-outline mt-6 inline-flex"><ArrowLeft className="w-4 h-4" /> Back to Placements</Link>
      </div>
    );
  }

  return (
    <div className="bg-[#fafaf7] min-h-screen" data-testid="page-placement-info">
      <div className="container-jcc py-10 md:py-14 max-w-4xl">
        <div className="mb-6">
          <Link to="/workspace/family-circle/placements" className="btn-outline !px-4 !py-2 text-sm" data-testid="placement-info-back">
            <ArrowLeft className="w-4 h-4" /> Back to Placements
          </Link>
        </div>
        <div className="mb-8">
          <div className="overline text-purple mb-3">Family Care Office</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">Getting Ready for Care</h1>
          <p className="mt-3 text-ink-muted leading-relaxed max-w-2xl">
            One shared record covering the practical information every authorised carer needs — so you never have
            to type it out again for each new connection. This doesn&rsquo;t repeat the rate, rota, breaks or
            payment terms already held in each individual Care Arrangement.
          </p>
          {record.updated_by_email && (
            <p className="mt-3 text-sm text-ink-soft" data-testid="placement-info-last-updated">
              Last updated by {record.updated_by_email} on{" "}
              {new Date(record.updated_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
        </div>

        <div className="space-y-6">
          {PLACEMENT_INFO_SECTIONS.map((s) => (
            <SectionEditor
              key={s.key}
              section={s}
              values={record}
              sectionStatus={record.section_status?.[s.key] || "not_started"}
              onSaved={setRecord}
            />
          ))}
          <SharingPanel placementId={placementId} />
        </div>
      </div>
    </div>
  );
}
